<template>
    <div class="refinance-wizard-container">
        <transition name="wizard-fade" mode="out-in">
            <component :is="currentPageComponent" />
        </transition>
    </div>
</template>

<script>
import { RefinanceStore, FLOWS } from './RefinanceStore';
import { RefinancePropsMixin } from './mixins/RefinancePropsMixin';
import { RefinanceStepMixin } from './mixins/RefinanceStepMixin';

// Import all pages
import RefinanceElegible from './pages/RefinanceElegible.vue';
import RefinanceConfiguration from './pages/RefinanceConfiguration.vue';
import RefinanceConfirmation from './pages/RefinanceConfirmation.vue';
import RefinanceComplete from './pages/RefinanceComplete.vue';
import NewLoanOffer from './pages/NewLoanOffer.vue';
import NewLoanConfirmation from './pages/NewLoanConfirmation.vue';
import NewLoanComplete from './pages/NewLoanComplete.vue';
import RefinanceUnavailable from './pages/RefinanceUnavailable.vue';
import RefinanceTemporaryUnavailable from './pages/RefinanceTemporaryUnavailable.vue';
import RefinanceNoPayroll from './pages/RefinanceNoPayroll.vue';

export default {
    name: 'RefinanceWizard',
    mixins: [RefinancePropsMixin, RefinanceStepMixin],
    components: {
        RefinanceElegible,
        RefinanceConfiguration,
        RefinanceConfirmation,
        RefinanceComplete,
        NewLoanOffer,
        NewLoanConfirmation,
        NewLoanComplete,
        RefinanceUnavailable,
        RefinanceTemporaryUnavailable,
        RefinanceNoPayroll
    },
    computed: {
        // Listen to the Store and return the current component name
        currentPageComponent() {
            return RefinanceStore.currentPage;
        }
    },
    created() {

        // "Hydrate": Inject initial props into the reactive Store
        RefinanceStore.init(this.$props);

        // No-payroll terminal state (refi payroll pre-check found no recent paycheck).
        // Takes priority over the generic not-eligible page — it carries its own actions
        // (Connect-a-Bank-with-Pay + email-a-paystub).
        if (this.storeData.noPayroll) {
            RefinanceStore.setFlow('NO_PAYROLL');
            return;
        }

        if(!this.storeData.eligible) {
            RefinanceStore.setFlow('TEMP_UNAVAILABLE');
            return;
        }

        if (this.canRefinance) {
            if (this.advancedRefi) {
                RefinanceStore.setFlow('NEW_LOAN');
            } else {
                RefinanceStore.setFlow('REFI');
            }
            return;
        }
        // we show this page when user has missed payment, but not in row
        RefinanceStore.setFlow('UNAVAILABLE');
    },
    mounted() {
        const appEl = document.getElementById('app');
        this._originalBgColor = appEl ? appEl.style.backgroundColor : null;
        if (appEl) appEl.style.backgroundColor = '#edf1f7';

        // _bouncing is true while we are waiting for the forward-popstate
        // that our own history.go(1) call will fire — we skip that event.
        this._bouncing = false;

        // Handle browser back/forward via popstate
        this._onPopState = (event) => {
            if (this._bouncing) {
                // This popstate was triggered by our own go(1) — ignore it.
                this._bouncing = false;
                return;
            }

            if (RefinanceStore.submissionLocked) {
                // Submission was successful — bounce the user forward instead of
                // stacking new history entries (pushState accumulates and can be
                // jumped through with rapid clicks; go(1) reuses the existing entry).
                const flow = FLOWS[RefinanceStore.currentFlowName];
                const lastStep = flow[flow.length - 1];
                RefinanceStore.currentPage = lastStep.comp;
                this._bouncing = true;
                window.history.go(1);
                return;
            }

            if (event.state && event.state.comp) {
                RefinanceStore.currentPage = event.state.comp;
            }
        };
        window.addEventListener('popstate', this._onPopState);
    },
    beforeDestroy() {
        const appEl = document.getElementById('app');
        if (appEl && this._originalBgColor !== null) {
            appEl.style.backgroundColor = this._originalBgColor;
        }

        window.removeEventListener('popstate', this._onPopState);
    }

}
</script>

<style scoped>
.wizard-fade-enter-active, .wizard-fade-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
}
.wizard-fade-enter {
    opacity: 0;
    transform: translateX(10px);
}
.wizard-fade-leave-to {
    opacity: 0;
    transform: translateX(-10px);
}
</style>